"use client";
import { Navbar, Modal, Button, Dropdown, HR } from "flowbite-react";
import { Outlet, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { login, getUser, logout, register_customer } from "../helpers/AuthService";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    PiHouseLineDuotone,
    PiTicketDuotone,
    PiUserCircleGearDuotone,
    PiSignInDuotone,
    PiSignOutDuotone,
    PiNewspaperDuotone,
} from "react-icons/pi";
import { IoTicketOutline } from "react-icons/io5";
import { useResource } from "../context/AppContext";
import { GoogleLogin } from "@react-oauth/google";

import dropdowntheme from "../themes/dropdown.json";
import navbartheme from "../themes/navbar.json";
import api from "../helpers/Api";
import FooterPage from "../pages/Footer";

export default function Menu() {
    const navigate = useNavigate();
    const { setUserLogged } = useResource();

    const [openModal, setOpenModal] = useState(false);
    const [user, setUser] = useState(null);

    const [isRegister, setIsRegister] = useState(false);
    const [isForgotPassword, setIsForgotPassword] = useState(false);

    const [authLoading, setAuthLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [logoutLoading, setLogoutLoading] = useState(false);

    const [formError, setFormError] = useState("");
    const [forgotSuccess, setForgotSuccess] = useState("");

    const loginSchema = z.object({
        email: z.string().email("Correo inválido"),
        password: z.string().min(6, "Mínimo 6 caracteres"),
    });

    const registerSchema = loginSchema.extend({
        first_name: z.string().min(2, "Nombre muy corto"),
        last_name: z.string().min(2, "Apellido muy corto"),
        gender: z.enum(["male", "female", "other"], { required_error: "Selecciona género" }),
        birth_date: z.string().nonempty("Ingresa tu fecha de nacimiento"),
        cellphone: z.string().regex(/^[0-9]{10}$/, "Teléfono inválido"),
    });

    const forgotSchema = z.object({
        email: z.string().email("Correo inválido"),
    });

    const schema = isForgotPassword ? forgotSchema : isRegister ? registerSchema : loginSchema;

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        resolver: zodResolver(schema),
    });

    useEffect(() => {
        reset();
        setFormError("");
        setForgotSuccess("");
    }, [isRegister, isForgotPassword]);

    useEffect(() => {
        const fetchData = async () => {
            setAuthLoading(true);
            try {
                const userLogged = await getUser();

                if (userLogged?.status === 401) {
                    setUser(null);
                    return;
                }

                setUser(userLogged);
            } catch (error) {
                setUser(null);
                if (error?.status === 401) {
                    navigate(`/`);
                }
            } finally {
                setAuthLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleLogout = async () => {
        try {
            setLogoutLoading(true);
            await logout(user);
            setUser(null);
            setUserLogged(null);
            navigate('/');
        } finally {
            setLogoutLoading(false);
        }
    };

    const onSubmit = async (data) => {
        setFormError("");
        setForgotSuccess("");
        setSubmitLoading(true);

        try {
            if (isRegister) {
                await register_customer(data);
                reset();
                setIsRegister(false);
                setOpenModal(false);
                return;
            }

            await login(data.email, data.password);

            const userLogged = await getUser();
            if (userLogged?.status === 401) {
                setFormError("No se pudo iniciar sesión. Verifica tus credenciales.");
                return;
            }

            setUser(userLogged);
            reset();
            setOpenModal(false);
            setIsRegister(false);
            setIsForgotPassword(false);
            setUserLogged(data.email);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || "Ocurrió un error. Intenta de nuevo.";
            setFormError(msg);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleForgotPassword = async (data) => {
        setFormError("");
        setForgotSuccess("");
        setSubmitLoading(true);

        try {
            await api.post("/Auth/Customer/ForgotPassword", { email: data.email });

            setForgotSuccess("Si el correo existe, te enviamos un link para cambiar tu contraseña.");
            reset({ email: "" });
        } catch (err) {
            setFormError("No se pudo enviar el correo. Intenta de nuevo.");
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        setFormError("");
        setForgotSuccess("");
        setGoogleLoading(true);

        try {
            const { credential } = credentialResponse;

            const res = await api.post(`/Auth/Customer/Google`, { token: credential }, { withCredentials: true });

            // La sesión vive en la cookie HttpOnly (no en localStorage).
            const userLogged = await getUser();
            setUser(userLogged);
            reset();
            setOpenModal(false);
            setIsRegister(false);
            setIsForgotPassword(false);
            setUserLogged(res?.data?.userLogged?.name || userLogged?.name || "");
        } catch (error) {
            console.error("Error login Google:", error);
            setFormError("No se pudo iniciar sesión con Google. Intenta de nuevo.");
        } finally {
            setGoogleLoading(false);
        }
    };

    return (
        <div>
            <Navbar theme={navbartheme} fluid className="bg-black text-white items-center">
                <Navbar.Brand as={Link} to="/" className="md:order-0">
                    <img src="/identity/MONOLOGO.png" className="mr-3 h-6 sm:h-9" alt="Monoticket" />
                </Navbar.Brand>

                <div className="flex items-center gap-2 md:order-2">
                    {user ? (
                        <Dropdown inline={true} theme={dropdowntheme} label={<span className="hidden sm:inline">{user.name}</span>} dismissOnClick={false}>
                            <Dropdown.Item>
                                <Link to="/myaccount">
                                    <span className="flex items-center text-white">
                                        <PiUserCircleGearDuotone size={22} className="mr-2" />
                                        Mi Cuenta
                                    </span>
                                </Link>
                            </Dropdown.Item>

                            <Dropdown.Item>
                                <Link to="/mytickets">
                                    <span className="flex items-center text-white">
                                        <IoTicketOutline size={22} className="mr-2" />
                                        Mis Boletos
                                    </span>
                                </Link>
                            </Dropdown.Item>

                            <HR className="my-2" />

                            <Dropdown.Item className="text-white" onClick={handleLogout} disabled={logoutLoading}>
                                <span className="flex items-center">
                                    {logoutLoading ? (
                                        <span className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <PiSignOutDuotone size={22} className="mr-2" />
                                    )}
                                    {logoutLoading ? "Cerrando..." : "Cerrar Sesión"}
                                </span>
                            </Dropdown.Item>
                        </Dropdown>
                    ) : (
                        <button
                            onClick={() => {
                                setFormError("");
                                setForgotSuccess("");
                                setOpenModal(true);
                                setIsRegister(false);
                                setIsForgotPassword(false);
                            }}
                            className="event-one__btn thm-btn"
                        >
                            <span className="flex items-center">
                                <PiSignInDuotone size={22} className="mr-2" />
                                <span className="hidden sm:inline">Iniciar Sesión</span>
                            </span>
                        </button>
                    )}

                    <Navbar.Toggle />
                </div>

                <Navbar.Collapse className="text-white md:order-1 md:flex md:w-auto">
                    <Navbar.Link as={Link} to="/">
                        <span className="flex items-center">
                            <PiHouseLineDuotone size={22} className="mr-2" />
                            Inicio
                        </span>
                    </Navbar.Link>

                    <Navbar.Link as={Link} to="/events">
                        <span className="flex items-center">
                            <PiTicketDuotone size={22} className="mr-2" />
                            Eventos
                        </span>
                    </Navbar.Link>

                    <Navbar.Link as={Link} to="/news">
                        <span className="flex items-center">
                            <PiNewspaperDuotone size={22} className="mr-2" />
                            Noticias
                        </span>
                    </Navbar.Link>
                </Navbar.Collapse>
            </Navbar>

            <div>
                <Outlet />
            </div>

            <FooterPage />

            <Modal
                show={openModal}
                size="md"
                onClose={() => {
                    setFormError("");
                    setForgotSuccess("");
                    setOpenModal(false);
                    setIsRegister(false);
                    setIsForgotPassword(false);
                }}
                popup
            >
                <Modal.Header className="bg-black text-white flex justify-center items-center">
                    <img src="/identity/MONOLOGO.png" className="h-6 sm:h-9" alt="Monoticket" />
                </Modal.Header>

                <Modal.Body className="bg-[#0f0e17] text-white">
                    <form
                        onSubmit={handleSubmit((data) => {
                            if (isForgotPassword) return handleForgotPassword(data);
                            return onSubmit(data);
                        })}
                        className="space-y-4"
                    >
                        {isForgotPassword ? (
                            <h3 className="text-xl font-medium text-white text-center font-display">
                                Escribe tu correo y te enviaremos un link para cambiar tu contraseña
                            </h3>
                        ) : (
                            <h3 className="text-xl font-medium text-white text-center font-display">{isRegister ? "Crear cuenta" : "Iniciar sesión"}</h3>
                        )}

                        {formError && (
                            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">{formError}</div>
                        )}

                        {forgotSuccess && (
                            <div className="rounded-lg border border-green-500/40 bg-green-500/10 p-3 text-sm text-green-200">{forgotSuccess}</div>
                        )}

                        {!isForgotPassword && isRegister && (
                            <>
                                <div>
                                    <label htmlFor="reg-first-name" className="block text-sm pb-2">Nombre</label>
                                    <input
                                        id="reg-first-name"
                                        autoComplete="given-name"
                                        {...register("first_name")}
                                        className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg w-full p-2.5 focus:ring-2 focus:ring-brand focus:border-transparent"
                                        placeholder="Juan"
                                    />
                                    {errors.first_name && <p className="text-red-400 text-xs pt-1">{errors.first_name.message}</p>}
                                </div>

                                <div>
                                    <label htmlFor="reg-last-name" className="block text-sm pb-2">Apellido</label>
                                    <input
                                        id="reg-last-name"
                                        autoComplete="family-name"
                                        {...register("last_name")}
                                        className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg w-full p-2.5 focus:ring-2 focus:ring-brand focus:border-transparent"
                                        placeholder="Pérez"
                                    />
                                    {errors.last_name && <p className="text-red-400 text-xs pt-1">{errors.last_name.message}</p>}
                                </div>

                                <div>
                                    <label htmlFor="reg-gender" className="block text-sm pb-2">Género</label>
                                    <select
                                        id="reg-gender"
                                        {...register("gender")}
                                        className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg w-full p-2.5 focus:ring-2 focus:ring-brand focus:border-transparent"
                                    >
                                        <option value="">Selecciona</option>
                                        <option value="male">Masculino</option>
                                        <option value="female">Femenino</option>
                                        <option value="other">Otro</option>
                                    </select>
                                    {errors.gender && <p className="text-red-400 text-xs pt-1">{errors.gender.message}</p>}
                                </div>

                                <div>
                                    <label htmlFor="reg-birth-date" className="block text-sm pb-2">Fecha de nacimiento</label>
                                    <input
                                        id="reg-birth-date"
                                        type="date"
                                        autoComplete="bday"
                                        {...register("birth_date")}
                                        className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg w-full p-2.5 focus:ring-2 focus:ring-brand focus:border-transparent"
                                    />
                                    {errors.birth_date && <p className="text-red-400 text-xs pt-1">{errors.birth_date.message}</p>}
                                </div>

                                <div>
                                    <label htmlFor="reg-cellphone" className="block text-sm pb-2">Teléfono</label>
                                    <input
                                        id="reg-cellphone"
                                        type="tel"
                                        autoComplete="tel"
                                        {...register("cellphone")}
                                        className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg w-full p-2.5 focus:ring-2 focus:ring-brand focus:border-transparent"
                                        placeholder="5512345678"
                                    />
                                    {errors.cellphone && <p className="text-red-400 text-xs pt-1">{errors.cellphone.message}</p>}
                                </div>
                            </>
                        )}

                        <div>
                            <label htmlFor="auth-email" className="block text-sm pb-2">Correo electrónico</label>
                            <input
                                id="auth-email"
                                type="email"
                                autoComplete="email"
                                {...register("email")}
                                className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg w-full p-2.5 focus:ring-2 focus:ring-brand focus:border-transparent"
                                placeholder="correo@ejemplo.com"
                            />
                            {errors.email && <p className="text-red-400 text-xs pt-1">{errors.email.message}</p>}
                        </div>

                        {!isForgotPassword && (
                            <div>
                                <label htmlFor="auth-password" className="block text-sm pb-2">Contraseña</label>
                                <input
                                    id="auth-password"
                                    type="password"
                                    autoComplete={isRegister ? "new-password" : "current-password"}
                                    {...register("password")}
                                    className="bg-gray-800 border border-gray-600 text-white text-sm rounded-lg w-full p-2.5 focus:ring-2 focus:ring-brand focus:border-transparent"
                                    placeholder="••••••••"
                                />
                                {errors.password && <p className="text-red-400 text-xs pt-1">{errors.password.message}</p>}

                                {!isRegister && (
                                    <p className="text-right mt-3 text-sm text-gray-400">
                                        <button
                                            type="button"
                                            className="text-brand hover:underline"
                                            onClick={() => {
                                                setIsForgotPassword(true);
                                                setFormError("");
                                                setForgotSuccess("");
                                            }}
                                        >
                                            ¿Olvidaste tu contraseña?
                                        </button>
                                    </p>
                                )}
                            </div>
                        )}

                        {isForgotPassword && (
                            <p className="text-right mt-1 text-sm text-gray-400">
                                <button
                                    type="button"
                                    className="text-brand hover:underline"
                                    onClick={() => {
                                        setIsForgotPassword(false);
                                        setFormError("");
                                        setForgotSuccess("");
                                    }}
                                >
                                    Volver al inicio de sesión
                                </button>
                            </p>
                        )}

                        {isForgotPassword ? (
                            <Button
                                type="submit"
                                disabled={submitLoading}
                                className="bg-brand hover:bg-brand-hover text-white w-full disabled:opacity-60"
                            >
                                <span className="flex items-center justify-center">
                                    {submitLoading && (
                                        <span className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    )}
                                    {submitLoading ? "Enviando..." : "Enviar"}
                                </span>
                            </Button>
                        ) : (
                            <div className="flex flex-col gap-2">
                                <Button
                                    type="submit"
                                    disabled={submitLoading || googleLoading}
                                    className="bg-brand hover:bg-brand-hover text-white w-full disabled:opacity-60"
                                >
                                    <span className="flex items-center justify-center">
                                        {submitLoading && (
                                            <span className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        )}
                                        {isRegister
                                            ? submitLoading
                                                ? "Registrando..."
                                                : "Registrarse"
                                            : submitLoading
                                                ? "Iniciando..."
                                                : "Iniciar sesión"}
                                    </span>
                                </Button>

                                <p className="text-center text-sm text-gray-400">
                                    {isRegister ? "¿Ya tienes cuenta?" : "¿No tienes cuenta?"}{" "}
                                    <button
                                        type="button"
                                        className="text-brand hover:underline"
                                        onClick={() => {
                                            setFormError("");
                                            setForgotSuccess("");
                                            setIsRegister(!isRegister);
                                            setIsForgotPassword(false);
                                        }}
                                    >
                                        {isRegister ? "Inicia sesión" : "Regístrate"}
                                    </button>
                                </p>

                                {!isRegister && (
                                    <div className={googleLoading ? "pointer-events-none opacity-60" : ""}>
                                        <GoogleLogin
                                            onSuccess={handleGoogleSuccess}
                                            onError={() => setFormError("Error al iniciar sesión con Google")}
                                        />
                                    </div>
                                )}

                                {googleLoading && (
                                    <div className="flex items-center justify-center gap-2 text-sm text-gray-300">
                                        <span className="h-4 w-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                                        Iniciando con Google...
                                    </div>
                                )}
                            </div>
                        )}
                    </form>
                </Modal.Body>
            </Modal>
        </div>
    );
}